<template>
  <div>
    <v-card class="mx-auto my-4" max-width="1000" :rounded="false" outlined>
      <v-toolbar color="blue-grey darken-1" dark flat>
        <v-btn icon to="/admin/users/">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title v-if="!isNew">{{ name }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-tooltip v-if="record" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" @click.stop="toggleDisableState()" icon>
              <v-icon v-if="record.enabled">mdi-account-off</v-icon>
              <v-icon v-else>mdi-account</v-icon>
            </v-btn>
          </template>
          <span v-if="record.enabled">{{ $t('admin.disable') }}</span>
          <span v-else>{{ $t('admin.enable') }}</span>
        </v-tooltip>
        <v-tooltip v-if="record" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="record"
              v-bind="attrs"
              v-on="on"
              @click.stop="confirmResetPassword = true"
              icon
              :disabled="!record.enabled"
            >
              <v-icon>mdi-lock-reset</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('admin.reset.tooltip') }}</span>
        </v-tooltip>
        <v-tooltip v-if="record" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="record"
              v-bind="attrs"
              v-on="on"
              @click.stop="confirmDelete = true"
              icon
              :disabled="record.enabled"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('admin.delete.tooltip') }}</span>
        </v-tooltip>
      </v-toolbar>
      <div v-if="loading" class="text-center">
        <v-progress-circular class="my-10" indeterminate color="primary"></v-progress-circular>
      </div>
      <v-container v-else fluid class="px-10 py-5">
        <v-form v-if="!!values" ref="form" v-model="valid" lazy-validation>
          <v-row class="py-6">
            <v-col v-if="isNew" sm="12" md="10" offset-md="1">
              {{ $t('admin.create') }}
            </v-col>
            <v-col v-else sm="12" md="10" offset-md="1">
              {{ $t('admin.update') }}
            </v-col>
          </v-row>
          <lean-form-block
            :initialValues="values"
            :resolved="resolved"
            :schema="theSchema"
            v-on:change="modifyRecord($event)"
          />
        </v-form>
      </v-container>
    </v-card>
    <v-btn
      v-if="isModified"
      elevation="2"
      color="blue-grey accent-4"
      dark
      fab
      fixed
      x-large
      bottom
      right
      @click="saveRecord"
    >
      <v-icon>mdi-content-save</v-icon>
    </v-btn>
    <v-dialog v-model="confirmDelete" persistent width="450">
      <v-card>
        <v-toolbar color="blue-grey darken-1" dark flat>
          <v-toolbar-title>{{ $t('delete.headline') }}</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pa-5">
          <p>{{ $t('delete.text') }}</p>
        </v-card-text>
        <v-card-actions class="pb-5">
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDelete = false">{{ $t('delete.no') }}</v-btn>
          <v-btn text @click="deleteUser()">{{ $t('delete.yes') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="confirmResetPassword" persistent width="450">
      <v-card>
        <v-toolbar color="blue-grey darken-1" dark flat>
          <v-toolbar-title>{{ $t('admin.reset.headline') }}</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pa-5">
          <p>{{ $t('admin.reset.text') }}</p>
        </v-card-text>
        <v-card-actions class="pb-5">
          <v-spacer></v-spacer>
          <v-btn text @click="confirmResetPassword = false">{{ $t('cancel') }}</v-btn>
          <v-btn text @click="resetPassword()">{{ $t('ok') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import LeanFormBlock from '@/components/LeanFormBlock.vue';
import { mapGetters, mapMutations, mapState } from 'vuex';
import LeanFormButtonDelete from '@/components/LeanFormButtonDelete';

export default {
  name: 'LeanAdminUserForm',
  components: { LeanFormButtonDelete, LeanFormBlock },
  data() {
    return {
      loading: false,
      record: null,
      resolved: null,
      values: null,
      confirmDelete: false,
      confirmResetPassword: false,
      isModified: false,
      valid: false,
      values: {},
      saved: false,
      schema: [
        {
          name: 'name',
          label: this.$t('admin.username'),
          type: 'string'
        },
        {
          name: 'email',
          label: this.$t('admin.email'),
          type: 'string'
        }
      ]
    };
  },
  props: {
    isNew: {
      type: Boolean
    },
    name: {
      type: String
    }
  },
  computed: {
    ...mapGetters(['workspaceBucket']),
    theSchema() {
      if (this.isNew) {
        return this.schema;
      } else {
        return this.schema.slice(1);
      }
    }
  },
  watch: {
    record() {
      this.values = JSON.parse(JSON.stringify(this.record));
    }
  },
  async mounted() {
    this.loading = true;
    if (!this.isNew) {
      const users = await this.$store.state.sdk.getUsers();
      this.record = users.find((user) => {
        return user.name === this.name;
      });
    }
    this.loading = false;
  },
  methods: {
    ...mapMutations({
      setSnackbar: 'setSnackbar'
    }),
    modifyRecord(data) {
      this.isModified = true;
      Object.keys(data.values).map((fieldName) => {
        this.values[fieldName] = data.values[fieldName];
      });
    },
    validate() {
      this.$refs.form.validate();
    },
    async toggleDisableState() {
      if (this.record.enabled) {
        await this.$store.state.sdk.disableUser(this.record.name);
        this.record.enabled = false;
      } else {
        await this.$store.state.sdk.enableUser(this.record.name);
        this.record.enabled = true;
      }
    },
    async deleteUser() {
      if (!this.record.enabled) {
        await this.$store.state.sdk.deleteUser(this.record.name);
        this.confirmDelete = false;
        this.$router.push({ path: `/admin/users` });
        this.setSnackbar({
          message: this.$t('admin.delete.success')
        });
      }
    },
    async resetPassword() {
      try {
        await this.$store.state.sdk.resetPassword(this.record.name);
      } catch (err) {
        this.confirmResetPassword = false;
        return this.setSnackbar({
          error: 'error',
          message: err
        });
      }
      this.confirmResetPassword = false;
      this.setSnackbar({
        message: this.$t('admin.reset.success')
      });
    },
    async saveRecord() {
      this.validate();
      if (this.valid) {
        try {
          if (this.isNew) {
            await this.$store.state.sdk.createUser(this.values.name, this.values.email);
          } else {
            await this.$store.state.sdk.updateUser(this.values.name, this.values.email);
          }
          this.setSnackbar({ message: 'Record saved.', type: 'success' });
          this.isModified = false;
          this.saved = true;
          this.$emit('saved', this.name);
          this.$router.push({ path: `/admin/users` });
        } catch (err) {
          this.setSnackbar({ message: err, type: 'error' });
        }
      } else {
        this.setSnackbar({ message: 'Invalid fields, please correct', type: 'error' });
      }
    }
  }
};
</script>
